import {usePandaService} from '@/composables/panda_service'
import {useUserStore} from '@/store/user'
import {USER_RIGHTS_MAPPING} from '~/assets/js/constants'
import {useRightsHelper} from '@/composables/rights_helper'

export const useProductAvailabilityPricesHelper = (productsRef) => {
  // if (!productsRef) {
  //   throw new Error('productsRef is required')
  // }
  const prices = ref([])
  const availabilities = ref([])
  const successorAvailabilities = ref([])
  const {userHasRight} = useRightsHelper()

  const loadingIndicators = ref({
    prices: [],
    availabilities: [],
    successorAvailabilities: []
  })

  const loadPrices = async () => {
    if (userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_PRICE)) {
      let productsToRequest = []

      if (productsRef.value?.products) {
        productsToRequest = productsRef.value.products.map(p => ({
          sku: p?.sku,
          quantity: p.maxPricedQty ?? 1
        }))
      } else if (productsRef.value?.searchResults) {
        productsToRequest = productsRef.value.searchResults.map(p => ({
          sku: p?.sku,
          quantity: p.maxPricedQty ?? 1
        }))
      }

      prices.value = []
      loadingIndicators.value.prices = []

      try {
        const response = await usePandaService().getMaterialPricesBulk(productsToRequest)
        if (response) {
          prices.value.push(...response)
        }
      } finally {
        loadingIndicators.value.prices.push(...productsToRequest.map(s => s?.sku))
      }
    }
  }

  const loadAvailabilities = async () => {
    if (userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)) {
      const nowUtcIsoString = new Date()
      let productsToRequest = []

      if (productsRef.value?.products) {
        productsToRequest = productsRef.value.products.map(p => ({
          sku: p?.sku,
          availabilityDate: nowUtcIsoString,
          quantity: 1
        }))
      } else if (productsRef.value?.searchResults) {
        productsToRequest = productsRef.value.searchResults.map(p => ({
          sku: p?.sku,
          availabilityDate: nowUtcIsoString,
          quantity: 1
        }))
      }

      availabilities.value = []
      loadingIndicators.value.availabilities = []

      try {
        const response = await usePandaService().getAvailabilitiesBulk(productsToRequest)
        if (response) {
          availabilities.value.push(...response)
        }
      } finally {
        loadingIndicators.value.availabilities.push(...productsToRequest.map(s => s?.sku))
      }
    }
  }

  const loadSuccessorAvailabilities = async () => {
    if (userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)) {
      const nowUtcIsoString = new Date()
      let productsToRequest = []

      const source = productsRef.value?.products ?? productsRef.value?.searchResults

      if (source) {
        productsToRequest = source
          .filter(p => p.salesAreas[0]?.successorSku)
          .map(p => ({
            sku: p.salesAreas[0].successorSku,
            availabilityDate: nowUtcIsoString,
            quantity: 1
          }))
      }

      successorAvailabilities.value = []
      loadingIndicators.value.successorAvailabilities = []

      try {
        const response = await usePandaService().getAvailabilitiesBulk(productsToRequest)
        if (response) {
          successorAvailabilities.value.push(...response)
        }
      } finally {
        loadingIndicators.value.successorAvailabilities.push(...productsToRequest.map(s => s.sku))
      }
    }
  }

  watch(productsRef, async () => {
    if (!useUserStore().loggedIn) {return}
    await nextTick()

    if (productsRef.value?.products || productsRef.value?.searchResults) {
      await Promise.all([
        loadPrices(),
        loadAvailabilities(),
        loadSuccessorAvailabilities()
      ])
    }
  }, {immediate: true})

  return {
    prices,
    availabilities,
    successorAvailabilities,
    loadingIndicators,
    loadPrices,
    loadAvailabilities,
    loadSuccessorAvailabilities
  }
}
